import { type PlanLimits, limitsByPlan } from "@/lib/limits";
import type Stripe from "stripe";

/**
 * The Teral Deploy plans we recognize, lowest to highest. Mirrored into
 * workspaces.deploy_plan; NULL (absence) means no Deploy plan.
 *
 * Adding a plan: create its Stripe price(s) tagged with metadata `plan=<name>`
 * and add the name here. No price ids are hardcoded, so re-pricing an existing
 * plan needs no change at all: the new price carries the same metadata and
 * existing subscriptions keep resolving (grandfathered).
 */
export const DEPLOY_PLANS = ["starter", "pro", "business"] as const;

/**
 * Whether a workspace without a Compute plan may deploy on the free plan. Keep
 * in sync with deploygate.FreePlanEnabled in pkg/deploy/deploygate, which is the
 * authoritative gate. The free plan's resource limits are limitsByPlan.free and
 * its monthly spend cap is deploygate.FreeSpendBudgetCents.
 */
export const FREE_PLAN_ENABLED = true;

/** Monthly compute credit of the free plan, in cents. Keep in sync with deploygate.FreeSpendBudgetCents. */
export const FREE_SPEND_BUDGET_CENTS = 200;
export type DeployPlan = (typeof DEPLOY_PLANS)[number];

type ComputeLimits = Pick<
  PlanLimits,
  | "logsRetentionDaysMax"
  | "logsAuditRetentionDaysMax"
  | "teamEnabled"
  | "cpuCoresMaxPerInstance"
  | "memoryMibMaxPerInstance"
  | "storageMibMaxPerInstance"
  | "buildsConcurrentMax"
>;

type ComputeOnlyLimits = Pick<
  ComputeLimits,
  | "cpuCoresMaxPerInstance"
  | "memoryMibMaxPerInstance"
  | "storageMibMaxPerInstance"
  | "buildsConcurrentMax"
>;

function computeLimitsFromPlan(limits: PlanLimits): ComputeLimits {
  return {
    logsRetentionDaysMax: limits.logsRetentionDaysMax,
    logsAuditRetentionDaysMax: limits.logsAuditRetentionDaysMax,
    teamEnabled: limits.teamEnabled,
    cpuCoresMaxPerInstance: limits.cpuCoresMaxPerInstance,
    memoryMibMaxPerInstance: limits.memoryMibMaxPerInstance,
    storageMibMaxPerInstance: limits.storageMibMaxPerInstance,
    buildsConcurrentMax: limits.buildsConcurrentMax,
  };
}

export function computeLimitsForPlan(plan: DeployPlan | null): ComputeLimits {
  return computeLimitsFromPlan(limitsByPlan[plan ?? "free"]);
}

/**
 * Returns the limit fields a Compute subscription may safely update. A paid
 * API plan owns the shared retention and team fields, so Compute must preserve
 * those while still applying its resource limits.
 */
export function computeLimitUpdateForPlan(
  plan: DeployPlan | null,
  preserveApiLimits: boolean,
): ComputeLimits | ComputeOnlyLimits {
  const limits = computeLimitsForPlan(plan);
  if (!preserveApiLimits) {
    return limits;
  }
  return {
    cpuCoresMaxPerInstance: limits.cpuCoresMaxPerInstance,
    memoryMibMaxPerInstance: limits.memoryMibMaxPerInstance,
    storageMibMaxPerInstance: limits.storageMibMaxPerInstance,
    buildsConcurrentMax: limits.buildsConcurrentMax,
  };
}

export function deployPlanGrantsTeam(plan: string | null): boolean {
  return plan === "pro" || plan === "business";
}

export function parseDeployPlan(plan: string | null): DeployPlan | null {
  return plan !== null && isDeployPlan(plan) ? plan : null;
}

function isDeployPlan(value: string): value is DeployPlan {
  return (DEPLOY_PLANS as readonly string[]).includes(value);
}

/**
 * Detects which Deploy plan, if any, a Stripe subscription carries.
 *
 * The plan-fee price is tagged in Stripe with metadata `plan=<plan>`, the
 * canonical "has a plan" marker. We scan the subscription's items (the plan-fee
 * sits alongside API items and metered Deploy prices) and return the first
 * recognized plan. price.metadata ships in the webhook payload, so this needs
 * no extra Stripe calls.
 *
 * Fails closed: an item tagged with an unrecognized plan is logged and ignored,
 * so a Stripe typo can never grant Deploy access. Returns null when no item
 * carries a recognized Deploy plan.
 */
export function detectDeployPlan(sub: Stripe.Subscription): DeployPlan | null {
  for (const item of sub.items?.data ?? []) {
    const raw = item.price?.metadata?.plan?.trim();
    if (!raw) {
      continue;
    }
    if (isDeployPlan(raw)) {
      return raw;
    }
    console.warn("Subscription item carries an unrecognized Deploy plan metadata value", {
      subscriptionId: sub.id,
      priceId: item.price?.id,
      deployPlan: raw,
    });
  }
  return null;
}
